import React, { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { useAccessToken } from "../hooks/useAuthentication";
import { useAccess } from "../hooks/useAccess";
import { useCheckpointAccess } from "../hooks/useCheckpointAccess";
import Header from "./Header";
import LoginRequired from "./LoginRequired";
import { NavBlocker } from "../lib/unsavedChanges";
import { RootCtx, type RootOutletContext } from "../lib/rootContext";
import { useSplitView, useWideEnoughForSplit } from "../lib/splitView";
import SplitWorkspace from "./split/SplitWorkspace";

// Root route element. Owns the app shell: the Header, the MSAL auth gate, the
// Suspense fallback for the lazy section chunks, and the router-level
// unsaved-changes blocker. The active section renders into <Outlet/>. Errors
// in a section are caught by the pathless errorElement route BELOW this one,
// so the Header/nav stay alive.
//
// The shell waits for TWO things before the first section mounts: the bearer
// (so no tab fetches before it exists) and the person's access from GET /me
// (so the nav does not flash every section and then hide most of them). Both
// have the same safety valve: after a few seconds the shell renders anyway.
//
// It is also the ONE place both split panes have in common: the Header, the
// auth gate, the access banners and the checkpoint lookup happen here, once,
// and the second pane (rendered by <SplitWorkspace/>) reuses them.

// Checkpoint access is resolved once here and handed to the Checkpoint route via
// Outlet context (rather than re-running the hook — and its fetch — per consumer).
// The same value goes onto RootCtx for the split pane, whose route elements are
// memoised and so cannot receive it as a prop.
export type { RootOutletContext };

const AuthLoading: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center py-32 text-sm text-content-subtle">
    <span className="mr-3 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    {label}
  </div>
);

const RootLayout: React.FC = () => {
  const { authReady } = useAccessToken();
  const { loading: accessLoading, error: accessError, preview, setPreview, refresh } = useAccess();
  const { hasCheckpointAccess, loading: checkpointLoading, checkCheckpointAccess } = useCheckpointAccess();
  const { open: splitOpen } = useSplitView();
  // Both hooks run unconditionally - short-circuiting one behind the other
  // would make the hook order depend on state.
  const wideEnough = useWideEnoughForSplit();
  const splitActive = splitOpen && wideEnough;

  const ready = authReady && !accessLoading;
  // The Fees app (/fees) is a standalone, full-screen workspace with its own
  // top bar: everything else here (auth gate, access, Suspense) still applies.
  const chromeless = useLocation().pathname.startsWith("/fees");

  // Safety valve: if MSAL never resolves a token (network blip / half-open
  // state) or /me never answers, render the shell anyway after a few seconds
  // rather than trapping the user on the spinner forever.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (ready) { setTimedOut(false); return; }
    const t = window.setTimeout(() => setTimedOut(true), 6000);
    return () => window.clearTimeout(t);
  }, [ready]);

  const outletContext: RootOutletContext = { hasCheckpointAccess, checkpointLoading, checkCheckpointAccess };

  const shell = (
    <>
      {!chromeless && <Header hasCheckpointAccess={hasCheckpointAccess} checkpointLoading={checkpointLoading} />}
      {preview && (
        <div className="flex items-center justify-center gap-3 bg-qw-teal/15 px-4 py-1.5 text-xs text-content">
          <span>
            Previewing the app as <strong>{preview.label}</strong>. The server still applies your own access.
          </span>
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="rounded border border-qw-teal px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-qw-teal hover:bg-qw-teal hover:text-white"
          >
            Exit preview
          </button>
        </div>
      )}
      {accessError && !preview && (
        <div className="flex items-center justify-center gap-3 bg-status-warn-soft px-4 py-1.5 text-xs text-status-warn">
          <span>Couldn&apos;t load your permissions ({accessError}). Showing everything; the server still checks each request.</span>
          <button type="button" onClick={refresh} className="underline">Retry</button>
        </div>
      )}
      {/* Split ON: the shell owns the viewport height and each pane scrolls
          itself. Split OFF: untouched — the page scrolls as it always has. */}
      <main className={splitActive ? "min-h-0 flex-1 overflow-hidden" : "flex-1"}>
        <SplitWorkspace>
          <Suspense fallback={<div className="p-8 text-sm text-content-subtle">Loading…</div>}>
            <Outlet context={outletContext} />
          </Suspense>
        </SplitWorkspace>
      </main>
      {/* Router-level unsaved-changes guard (nav links, in-app links, back/forward). */}
      <NavBlocker />
    </>
  );

  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";
  const label = authReady ? "Loading your workspace…" : "Signing you in…";

  return (
    <RootCtx.Provider value={outletContext}>
      <div
        className={`bg-app font-montserrat text-content ${
          splitActive ? "flex h-screen flex-col overflow-hidden print:h-auto print:overflow-visible" : "min-h-screen"
        }`}
      >
        {bypassAuth ? (
          // Local dev only — VITE_BYPASS_AUTH must never be true in a production build.
          accessLoading && !timedOut ? <AuthLoading label={label} /> : shell
        ) : (
          <>
            <AuthenticatedTemplate>{ready || timedOut ? shell : <AuthLoading label={label} />}</AuthenticatedTemplate>
            <UnauthenticatedTemplate><LoginRequired /></UnauthenticatedTemplate>
          </>
        )}
      </div>
    </RootCtx.Provider>
  );
};

export default RootLayout;
